/**
 * Client variant-artwork upload (BLU-662).
 *
 * Self-contained upload state machine for ONE (locationId, sku, side) artwork
 * target. A focused fork of `useDesignUpload`'s mint -> PUT shape, extended for
 * the BLU-662 Mode A contract:
 *   1. validate the file client-side (PNG-only, <= 25 MB, non-empty),
 *   2. content-hash it with SubtleCrypto (sha256) — the SAME digest the server
 *      re-verifies at commit, so the browser's claim is only used to build the
 *      content-addressed object key,
 *   3. mint a signed PUT (`mintVariantArtworkUploadUrl`),
 *   4. PUT the bytes direct to the public bucket with a MATCHING Content-Type
 *      (`image/png` — the ONLY header the backend signs; `gcs-writer.ts`
 *      signs contentType alone and does NOT sign Cache-Control, so we never add
 *      a Cache-Control header — an unsigned header risks SignatureDoesNotMatch
 *      and would not be applied anyway; long-cache immutability is a bucket
 *      default / follow-up, not a client concern here),
 *   5. commit (`commitVariantArtworkUpload`) — the server re-downloads,
 *      re-hashes, verifies == our sha256, then writes the durable public URL to
 *      the variant's front_artwork/back_artwork metafield.
 *
 * The one non-obvious failure mode is `publish_failed`: the PUT SUCCEEDED but
 * the Shopify metafield write did not. The backend KEEPS the object (retry is
 * idempotent on the same content-addressed key), so `retryPublish` re-runs the
 * commit ALONE — this is the "uploaded but not published to Shopify, retry"
 * state the UI surfaces. A full re-drop is still available as the fallback for
 * the rare transit-corruption case (where the object was deleted server-side).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { uploadRefusalKey } from '../lib/upload-refusal';

// Exported for `useColourArtworkUpload` (per-colour fan-out) — the two hooks
// MUST validate against the same limits the mint schema enforces server-side.
export const MAX_ARTWORK_BYTES = 25 * 1024 * 1024;
export const ARTWORK_CONTENT_TYPE = 'image/png';

export type ArtworkSide = 'front' | 'back';

export type ArtworkUploadPhase =
  | 'idle'
  | 'hashing'
  | 'uploading'
  | 'publishing'
  | 'success'
  | 'error';

export type ArtworkUploadErrorKind =
  // Client-side validation (no bytes sent):
  | 'invalid_type'
  | 'too_large'
  | 'empty_file'
  | 'hash_unavailable'
  // Server / transport:
  | 'mint_failed'
  | 'upload_failed'
  // PUT succeeded, metafield publish did not (object kept, retry is idempotent):
  | 'publish_failed'
  | 'unknown';

export interface VariantArtworkUploadError {
  kind: ArtworkUploadErrorKind;
  message: string;
}

export interface VariantArtworkUploadState {
  phase: ArtworkUploadPhase;
  /** 0..1 — meaningful during `uploading` (real XHR upload progress). */
  progress: number;
  /** Object URL of the staged file for the thumbnail; revoked on reset/unmount. */
  previewUrl: string | null;
  fileName: string | null;
  byteSize: number | null;
  /** Durable public URL — set once `success`. */
  publicUrl: string | null;
  error: VariantArtworkUploadError | null;
}

export interface UseVariantArtworkUploadArgs {
  locationId: string;
  /** Design-bearing Shopify variant SKU (`shopify_sku`), NOT the base garment SKU. */
  sku: string;
  side: ArtworkSide;
  onUploaded?: (publicUrl: string) => void;
}

export interface VariantArtworkUploadApi {
  state: VariantArtworkUploadState;
  /** Full flow: validate -> hash -> mint -> PUT -> commit. */
  upload: (file: File) => Promise<void>;
  /** Commit-only retry after a `publish_failed` (fast path; object already uploaded). */
  retryPublish: () => Promise<void>;
  /** Clear back to idle and revoke the staged preview URL. */
  reset: () => void;
  /** True when the last error is a failed publish and `retryPublish` is valid. */
  canRetryPublish: boolean;
}

const IDLE_STATE: VariantArtworkUploadState = {
  phase: 'idle',
  progress: 0,
  previewUrl: null,
  fileName: null,
  byteSize: null,
  publicUrl: null,
  error: null,
};

function isPngFile(file: File): boolean {
  if (file.type) return file.type === ARTWORK_CONTENT_TYPE;
  return file.name.toLowerCase().endsWith('.png');
}

// Exported for `useColourArtworkUpload` (per-colour fan-out) — it hashes the
// dropped PNG ONCE with this exact helper, then reuses the digest across every
// SKU it publishes to. Kept as one implementation so the browser's sha256 can
// never disagree with the server's re-verify between the two hooks.
export async function sha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * PUT via XHR (not fetch) so the browser can report real upload progress —
 * `fetch` has no upload-progress event. Resolves on 2xx, rejects otherwise.
 *
 * Exported for `useColourArtworkUpload` so both the single-cell and the
 * per-colour fan-out drive the exact same signed-PUT transport (identical
 * abort + progress semantics), rather than a divergent copy.
 */
export function putWithProgress(
  url: string,
  file: File,
  contentType: string,
  onProgress: (fraction: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('network error during upload'));
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

export function useVariantArtworkUpload({
  locationId,
  sku,
  side,
  onUploaded,
}: UseVariantArtworkUploadArgs): VariantArtworkUploadApi {
  const { t } = useTranslation('productSync');
  const mint = trpc.productSync.mintVariantArtworkUploadUrl.useMutation();
  const commit = trpc.productSync.commitVariantArtworkUpload.useMutation();

  const [state, setState] = useState<VariantArtworkUploadState>(IDLE_STATE);

  // Monotonic run id — a stale async result from a superseded upload (a second
  // drop, or a reset) must never clobber newer state.
  const runIdRef = useRef(0);
  // Live object URL, mirrored for unmount cleanup without re-subscribing.
  const previewUrlRef = useRef<string | null>(null);
  // Retained for the commit-only `retryPublish` fast path.
  const lastAttemptRef = useRef<{ sha256: string } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      runIdRef.current += 1;
      abortRef.current?.abort();
      revokePreview();
    };
  }, [revokePreview]);

  const failWith = useCallback(
    (id: number, kind: ArtworkUploadErrorKind, message: string, keepPreview: boolean) => {
      if (runIdRef.current !== id) return;
      setState((prev) => ({
        ...prev,
        phase: 'error',
        progress: 0,
        error: { kind, message },
        // A rejection on a NEW file must never wipe an already-published
        // side (DESIGN §15.3 / VariantArtworkUploader's `showFilled`
        // contract) — `publicUrl` is the ONLY durable-URL source until the
        // caller's `initialUrl` prop catches up on refetch, so it (and the
        // staged preview, when `keepPreview`) must survive an error path.
        ...(keepPreview ? {} : { previewUrl: null, fileName: null, byteSize: null, publicUrl: null }),
      }));
      if (!keepPreview) revokePreview();
    },
    [revokePreview],
  );

  const serverMessage = useCallback(
    (err: unknown, fallbackKey: string): string => {
      const refusal = uploadRefusalKey(err);
      if (refusal) return t(refusal);
      return err instanceof Error && err.message ? err.message : t(fallbackKey);
    },
    [t],
  );

  const upload = useCallback(
    async (file: File) => {
      // --- client-side validation (no bytes sent) --------------------------
      // A rejection here fires BEFORE any run is staged, so `prev` still
      // reflects whatever was durably published this session — reset only
      // the staged-file fields (previewUrl/fileName/byteSize), never
      // `publicUrl`, or an already-committed side vanishes from the card
      // (see `failWith`'s docs; same invariant applies here).
      if (!isPngFile(file)) {
        // Bump the run id so a validation reject cancels any in-flight run.
        runIdRef.current += 1;
        revokePreview();
        setState((prev) => ({
          ...prev,
          phase: 'error',
          progress: 0,
          previewUrl: null,
          fileName: null,
          byteSize: null,
          error: { kind: 'invalid_type', message: t('variantArtwork.errInvalidType') },
        }));
        return;
      }
      if (file.size === 0) {
        runIdRef.current += 1;
        revokePreview();
        setState((prev) => ({
          ...prev,
          phase: 'error',
          progress: 0,
          previewUrl: null,
          fileName: null,
          byteSize: null,
          error: { kind: 'empty_file', message: t('variantArtwork.errEmpty') },
        }));
        return;
      }
      if (file.size > MAX_ARTWORK_BYTES) {
        runIdRef.current += 1;
        revokePreview();
        setState((prev) => ({
          ...prev,
          phase: 'error',
          progress: 0,
          previewUrl: null,
          fileName: null,
          byteSize: null,
          error: { kind: 'too_large', message: t('variantArtwork.errTooLarge') },
        }));
        return;
      }

      if (!window.crypto?.subtle) {
        runIdRef.current += 1;
        revokePreview();
        setState((prev) => ({
          ...prev,
          phase: 'error',
          progress: 0,
          previewUrl: null,
          fileName: null,
          byteSize: null,
          error: { kind: 'hash_unavailable', message: t('variantArtwork.errHashUnavailable') },
        }));
        return;
      }

      // --- stage preview + start a new run ---------------------------------
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const id = (runIdRef.current += 1);

      revokePreview();
      const previewUrl = URL.createObjectURL(file);
      previewUrlRef.current = previewUrl;
      lastAttemptRef.current = null;
      // Merge, don't replace: `prev.publicUrl` may be the ONLY record of a
      // side already published this session (before `initialUrl` catches up
      // on refetch). Leaving it in place here is what lets `failWith` below
      // fall back to it on a mid-flight mint/upload/publish failure —
      // `state.previewUrl` (the new file, set above) still takes display
      // priority over it while this run is in flight.
      setState((prev) => ({
        ...prev,
        phase: 'hashing',
        progress: 0,
        previewUrl,
        fileName: file.name,
        byteSize: file.size,
        error: null,
      }));

      // --- hash ------------------------------------------------------------
      let sha256: string;
      try {
        sha256 = await sha256Hex(file);
      } catch {
        failWith(id, 'hash_unavailable', t('variantArtwork.errHashUnavailable'), true);
        return;
      }
      if (runIdRef.current !== id) return;

      // --- mint ------------------------------------------------------------
      let minted: { uploadUrl: string; key: string; publicUrl: string };
      try {
        minted = await mint.mutateAsync({
          locationId,
          sku,
          side,
          sha256,
          byteSize: file.size,
          contentType: ARTWORK_CONTENT_TYPE,
        });
      } catch (err) {
        failWith(id, 'mint_failed', serverMessage(err, 'variantArtwork.errMint'), true);
        return;
      }
      if (runIdRef.current !== id) return;

      // --- PUT (with real progress) ----------------------------------------
      setState((prev) => (runIdRef.current === id ? { ...prev, phase: 'uploading', progress: 0 } : prev));
      try {
        await putWithProgress(
          minted.uploadUrl,
          file,
          ARTWORK_CONTENT_TYPE,
          (fraction) => setState((prev) => (runIdRef.current === id && prev.phase === 'uploading' ? { ...prev, progress: fraction } : prev)),
          controller.signal,
        );
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        failWith(id, 'upload_failed', t('variantArtwork.errUpload'), true);
        return;
      }
      if (runIdRef.current !== id) return;

      // --- commit (publish) ------------------------------------------------
      lastAttemptRef.current = { sha256 };
      setState((prev) => (runIdRef.current === id ? { ...prev, phase: 'publishing', progress: 1 } : prev));
      try {
        const res = await commit.mutateAsync({ locationId, sku, side, sha256 });
        if (runIdRef.current !== id) return;
        setState((prev) => ({ ...prev, phase: 'success', progress: 1, publicUrl: res.publicUrl, error: null }));
        onUploaded?.(res.publicUrl);
      } catch (err) {
        failWith(id, 'publish_failed', serverMessage(err, 'variantArtwork.errPublish'), true);
      }
    },
    [commit, failWith, locationId, mint, onUploaded, revokePreview, serverMessage, side, sku, t],
  );

  const retryPublish = useCallback(async () => {
    const last = lastAttemptRef.current;
    if (!last) return;
    const id = (runIdRef.current += 1);
    setState((prev) => ({ ...prev, phase: 'publishing', error: null }));
    try {
      const res = await commit.mutateAsync({ locationId, sku, side, sha256: last.sha256 });
      if (runIdRef.current !== id) return;
      setState((prev) => ({ ...prev, phase: 'success', progress: 1, publicUrl: res.publicUrl, error: null }));
      onUploaded?.(res.publicUrl);
    } catch (err) {
      failWith(id, 'publish_failed', serverMessage(err, 'variantArtwork.errPublish'), true);
    }
  }, [commit, failWith, locationId, onUploaded, serverMessage, side, sku]);

  const reset = useCallback(() => {
    runIdRef.current += 1;
    abortRef.current?.abort();
    lastAttemptRef.current = null;
    revokePreview();
    setState(IDLE_STATE);
  }, [revokePreview]);

  return {
    state,
    upload,
    retryPublish,
    reset,
    canRetryPublish: state.phase === 'error' && state.error?.kind === 'publish_failed',
  };
}
